<template>
    <div class="pt-page">

        <!-- LOADER -->
        <div v-if="loading" class="flex flex-col items-center justify-center py-24">
            <div class="flex gap-2 mb-4">
                <div class="w-2 h-2 bg-orange-600 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                <div class="w-2 h-2 bg-orange-600 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                <div class="w-2 h-2 bg-orange-600 rounded-full animate-bounce"></div>
            </div>
            <p class="text-sm text-gray-500">Chargement…</p>
        </div>

        <div v-else class="pt-layout">

            <!-- SIDEBAR -->
            <div class="pt-sidebar">
                <div class="pt-sidebar-header">Catégorie appareil</div>
                <div class="pt-folder-list">
                    <div
                        v-for="cat in appareilsType"
                        :key="cat.id"
                        class="pt-folder-item"
                        :class="{ active: activeId === cat.id }"
                        @click="selectCat(cat.id)">
                        <img
                            v-if="cat.icon"
                            :src="cat.icon"
                            class="pt-folder-icon"
                            alt=""
                            @error="$event.target.style.display='none'" />
                        <span class="pt-folder-label">{{ cat.label }}</span>
                    </div>
                </div>
            </div>

            <!-- MAIN -->
            <div class="pt-main">

                <div v-if="!activeId" class="pt-no-sel">
                    <div class="pt-no-sel-icon"><i class="fas fa-folder-open"></i></div>
                    <p>Sélectionnez une catégorie<br>pour configurer ses tarifs</p>
                </div>

                <div v-else class="pt-main-inner">

                    <!-- Breadcrumb -->
                    <div class="pt-breadcrumb">
                        <img
                            v-if="activeCat.icon"
                            :src="activeCat.icon"
                            class="pt-bc-icon"
                            @error="$event.target.style.display='none'" />
                        <span class="pt-bc-cat">{{ activeCat.label }}</span>
                    </div>

                    <!-- Formulaire -->
                    <div>
                        <div class="pt-section-title">Tarifs</div>

                        <div class="pt-fields">
                            <div
                                v-for="field in fields"
                                :key="field.column"
                                class="pt-field-row">

                                <!-- Label -->
                                <div class="pt-field-info">
                                    <div class="pt-field-label">{{ field.label }}</div>
                                    <div class="pt-field-desc">{{ field.desc }}</div>
                                </div>

                                <!-- Input -->
                                <div class="pt-field-control">

                                    <!-- Mode édition -->
                                    <div v-if="editingField === field.column" class="pt-edit-wrap">
                                        <div class="pt-price-input-box">
                                            <input
                                                :id="`inp-${field.column}`"
                                                v-model.number="editValue"
                                                class="pt-price-input"
                                                type="number"
                                                placeholder="0.00"
                                                step="0.01"
                                                min="0"
                                                @keydown.enter="confirmEdit(field.column)"
                                                @keydown.escape="cancelEdit" />
                                            <span class="pt-price-unit">€</span>
                                        </div>
                                        <button class="pt-btn-ok" title="Valider" @click="confirmEdit(field.column)">
                                            <i class="fas fa-check" style="font-size:11px"></i>
                                        </button>
                                        <button class="pt-btn-x" title="Annuler" @click="cancelEdit">
                                            <i class="fas fa-times" style="font-size:11px"></i>
                                        </button>
                                    </div>

                                    <!-- Mode affichage -->
                                    <div
                                        v-else
                                        class="pt-price-display"
                                        @click="startEdit(field.column)">
                                        <span :class="['pt-price-val', !activeCat[field.column] && 'zero']">
                                            {{ activeCat[field.column]
                                                ? formatPrice(activeCat[field.column]) + ' €'
                                                : 'Non défini' }}
                                        </span>
                                        <span class="pt-edit-pencil">✏</span>
                                    </div>

                                    <!-- Statut inline -->
                                    <span v-if="fieldStates[field.column] === 'saving'" class="pt-status-saving">
                                        <i class="fas fa-spinner fa-spin" style="font-size:10px"></i> Enregistrement…
                                    </span>
                                    <span v-else-if="fieldStates[field.column] === 'saved'" class="pt-status-saved">
                                        <i class="fas fa-check-circle" style="font-size:11px"></i> Enregistré
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ParametreTarifs',

    data() {
        return {
            loading: false,
            appareilsType: [],

            // Définir ici tous les champs tarif — facile d'en ajouter d'autres plus tard
            fields: [
                {
                    column: 'tarif_contrat_garantie',
                    label: 'Contrat de garantie',
                    desc: 'Tarif annuel facturé au client pour la garantie',
                },
            ],

            activeId: null,
            editingField: null,
            editValue: null,
            fieldStates: {},
        }
    },

    computed: {
        activeCat() {
            return this.appareilsType.find(a => a.id === this.activeId) || null
        },
    },

    methods: {
        formatPrice(val) {
            return parseFloat(val).toFixed(2).replace('.', ',')
        },

        selectCat(id) {
            if (this.activeId === id) return
            this.activeId = id
            this.editingField = null
            this.editValue = null
            this.fieldStates = {}
        },

        startEdit(column) {
            this.editingField = column
            this.editValue = this.activeCat[column] || null
            this.$nextTick(() => {
                const inp = document.getElementById(`inp-${column}`)
                if (inp) { inp.focus(); inp.select() }
            })
        },

        cancelEdit() {
            this.editingField = null
            this.editValue = null
        },

        async confirmEdit(column) {
            const newVal = parseFloat(this.editValue) || 0
            this.activeCat[column] = newVal
            this.editingField = null
            this.editValue = null

            this.$set(this.fieldStates, column, 'saving')

            const response = await this.$request.post('categorie-appareil/edit-tarif', {
                line_id: this.activeCat.id,
                column: column,
                amount: newVal,
            })

            if (response == 1) {
                this.$set(this.fieldStates, column, 'saved')
                setTimeout(() => this.$delete(this.fieldStates, column), 2000)
            } else {
                this.$delete(this.fieldStates, column)
                this.$toast.error('Erreur lors de l\'enregistrement')
            }
        },

        async load() {
            this.loading = true
            this.appareilsType = await this.$request.post('/reference/get-categories')
            this.loading = false
        },
    },

    mounted() {
        this.load()
    },
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');

.pt-page { font-family: 'DM Sans', sans-serif; padding: 32px; min-height: 100vh; background: #F5F4F1; color: #1A1916; }

.pt-layout { display: grid; grid-template-columns: 210px 1fr; background: #fff; border: 1px solid #E4E2DC; border-radius: 12px; overflow: hidden; box-shadow: 0 1px 4px rgba(0,0,0,.07); }

.pt-sidebar { border-right: 1px solid #E4E2DC; min-height: 480px; background: #FAFAF8; }
.pt-sidebar-header { padding: 13px 14px; border-bottom: 1px solid #E4E2DC; font-size: 10.5px; font-weight: 600; letter-spacing: .8px; text-transform: uppercase; color: #A8A49D; }
.pt-folder-list { padding: 6px; }
.pt-folder-item { display: flex; align-items: center; gap: 8px; padding: 8px 10px; border-radius: 6px; cursor: pointer; transition: background .15s, color .15s; font-size: 13px; color: #6B6860; user-select: none; }
.pt-folder-item:hover { background: #F0EFE9; color: #1A1916; }
.pt-folder-item.active { background: #1A1916; color: #fff; }
.pt-folder-icon { width: 16px; height: 16px; object-fit: contain; flex-shrink: 0; opacity: .7; }
.pt-folder-item.active .pt-folder-icon { opacity: 1; filter: brightness(0) invert(1); }
.pt-folder-label { flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.pt-main { display: flex; flex-direction: column; background: #fff; min-height: 480px; }
.pt-no-sel { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 60px 24px; text-align: center; gap: 12px; }
.pt-no-sel-icon { font-size: 28px; color: #D4D1CB; }
.pt-no-sel p { font-size: 13.5px; color: #B0ACA5; line-height: 1.6; }

.pt-main-inner { padding: 24px; flex: 1; display: flex; flex-direction: column; gap: 20px; }

.pt-breadcrumb { display: flex; align-items: center; gap: 7px; font-size: 12.5px; }
.pt-bc-icon { width: 14px; height: 14px; object-fit: contain; opacity: .45; }
.pt-bc-cat { color: #1A1916; font-weight: 600; }

.pt-section-title { font-size: 10.5px; font-weight: 600; letter-spacing: .7px; text-transform: uppercase; color: #A8A49D; margin-bottom: 4px; }

.pt-fields { }
.pt-field-row { display: flex; align-items: center; padding: 14px 0; border-bottom: 1px solid #F0EFE9; gap: 16px; }
.pt-field-row:last-child { border-bottom: none; }
.pt-field-info { flex: 1; }
.pt-field-label { font-size: 13.5px; color: #1A1916; }
.pt-field-desc { font-size: 11.5px; color: #A8A49D; margin-top: 2px; }

.pt-field-control { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }

.pt-price-display { display: inline-flex; align-items: center; justify-content: space-between; gap: 8px; cursor: pointer; padding: 7px 11px; border-radius: 6px; border: 1px solid #E4E2DC; background: #FAFAF8; transition: all .15s; min-width: 120px; }
.pt-price-display:hover { border-color: #C8C4BC; background: #F0EFE9; }
.pt-price-val { font-size: 14px; font-weight: 500; color: #1A1916; font-family: 'DM Mono', monospace; }
.pt-price-val.zero { color: #C8C4BC; font-style: italic; font-family: 'DM Sans', sans-serif; font-weight: 400; font-size: 13px; }
.pt-edit-pencil { font-size: 11px; color: #C8C4BC; }

.pt-edit-wrap { display: flex; align-items: center; gap: 6px; }
.pt-price-input-box { display: flex; align-items: center; border: 1.5px solid #3B82F6; border-radius: 6px; overflow: hidden; background: #fff; }
.pt-price-input { border: none; outline: none; background: transparent; font-size: 14px; font-weight: 500; color: #1A1916; padding: 6px 8px; width: 80px; font-family: 'DM Mono', monospace; }
.pt-price-unit { padding: 6px 8px 6px 0; font-size: 13px; color: #3B82F6; font-weight: 500; }
.pt-btn-ok { width: 28px; height: 28px; border-radius: 5px; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; background: #1A1916; color: #fff; transition: all .15s; flex-shrink: 0; }
.pt-btn-ok:hover { background: #3D3C39; }
.pt-btn-x { width: 28px; height: 28px; border-radius: 5px; border: 1px solid #E4E2DC; cursor: pointer; display: flex; align-items: center; justify-content: center; background: transparent; color: #A8A49D; transition: all .15s; flex-shrink: 0; }
.pt-btn-x:hover { background: #F5F4F1; color: #1A1916; }

input[type='number']::-webkit-inner-spin-button,
input[type='number']::-webkit-outer-spin-button { -webkit-appearance: none; }
input[type='number'] { -moz-appearance: textfield; }

.pt-status-saving { font-size: 11.5px; color: #A8A49D; display: flex; align-items: center; gap: 4px; white-space: nowrap; }
.pt-status-saved { font-size: 11.5px; color: #15803D; display: flex; align-items: center; gap: 4px; white-space: nowrap; }
</style>